import $ from 'jquery';
import { AdGroupBidLandscape } from '@bingads-webui/bid-landscape';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { showPopup } from '@bingads-webui/automatic-positioning-popover';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { BiddingStrategyToRangeKey, BiddingStrategyToBidParam } from '@bingads-webui-campaign/audience-campaign-shared';
import { http } from '@bingads-webui/http-util';
import * as url from '@bingads-webui/url-util';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { generateGuid } from '@bingads-webui-universal/primitive-utilities';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-groups-tab');
const popupUtil = globalContext.require('module/service/shared/popup');
const appConfig = globalContext.require('component/config/index');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const senario = instrumentation.adgroupBidlandscape.create();
const i18nErrors = globalContext.require('component/api-errors-campaign/index');
const jsData = globalContext.require('component/js-data-campaign/index');
const odata = globalContext.require('module/service/communication/channel/odata');
const currencyCode = appConfig.get('AccountCurrencyCode');
const permissions = appConfig.get('Permissions');

function showError(msg) {
  const alertHelper = new AlertHelper({ i18n });
  alertHelper.showError(msg);
}

function applyError(response) {
  let error;
  const unexpectedError = i18n.getString('ErrorNumber_-1');

  if (!response || !response.errors) {
    error = unexpectedError;
  } else {
    const errors = i18nErrors.getBulkApiErrors(response.errors, 'newadgroup');

    if (!errors || !errors[0] || !errors[0].message) {
      error = unexpectedError;
    } else {
      error = errors[0].message;
    }
  }

  showError(error);
}

function defaultApply(
  adgroup,
  adgroupBidLandscapeView,
  activity,
  applyCallback,
  blockerEl,
  trackingId
) {
  const {
    Id, SearchBid, Name, Campaign, CpmBid, CpvBid, DisplayBiddingStrategyType: bidType,
  } = adgroup;
  let oldValue = SearchBid;
  const newValue = i18n.parseDecimal(adgroupBidLandscapeView.valueToApply());
  let bidKV = { SearchBid: newValue };
  const toUpdate = {
    Id,
    Campaign: { Id: Campaign.Id },
  };
  if (permissions.IsAdgroupLandscapeV2Enabled) {
    if (bidType === 'ManualCpm') {
      bidKV = { CpmBid: newValue };
      oldValue = CpmBid;
    } else if (bidType === 'ManualCpv') {
      bidKV = { CpvBid: newValue };
      oldValue = CpvBid;
    }
  }
  const currency = appConfig.get('AccountCurrencyCode');
  const isCustomBid = adgroupBidLandscapeView.viewModel.currentSelectedId()
    === adgroupBidLandscapeView.viewModel.landscapePointsLength();
  const startDate = adgroupBidLandscapeView.viewModel.startDateString();
  const endDate = adgroupBidLandscapeView.viewModel.endDateString();
  jsData.AdGroup
    .update(Id, { ...toUpdate, ...bidKV }, { adapter: 'bulk' })
    .then(() => {
      popupUtil.close();
      applyCallback(bidKV);
      activity.trace(
        `Apply success for ${Id} [${Name}]: ${oldValue} -> ${newValue}, Currency:${currency}, DataDate: ${startDate}~${endDate}, TrackingId: ${trackingId}, IsCustomBid: ${isCustomBid}, DisplayBiddingStrategyType: ${bidType}`,
        'defaultApply'
      );
    })
    .catch((response) => {
      applyError(response);
    })
    .finally(withUiBlock(blockerEl));
}

function customApply(
  adgroup,
  adgroupBidLandscapeView,
  activity,
  scope,
  applyCallback,
  blockerEl,
  trackingId
) {
  const { customerId, accountId } = scope;
  const campaignId = adgroup.Campaign.Id;
  const {
    Id, SearchBid, Name, CpmBid, CpvBid, DisplayBiddingStrategyType: bidType,
  } = adgroup;
  const currency = appConfig.get('AccountCurrencyCode');
  const newBidStr = adgroupBidLandscapeView.valueToApply();
  const newBid = i18n.parseDecimal(newBidStr);
  const isCustomBid = adgroupBidLandscapeView.viewModel.currentSelectedId()
    === adgroupBidLandscapeView.viewModel.landscapePointsLength();
  const startDate = adgroupBidLandscapeView.viewModel.startDateString();
  const endDate = adgroupBidLandscapeView.viewModel.endDateString();

  let oldValue = SearchBid;
  let bidKV = { SearchBid: newBid };
  if (permissions.IsAdgroupLandscapeV2Enabled) {
    if (bidType === 'ManualCpm') {
      bidKV = { CpmBid: newBid };
      oldValue = CpmBid;
    } else if (bidType === 'ManualCpv') {
      bidKV = { CpvBid: newBid };
      oldValue = CpvBid;
    }
  }

  const uri = `Advertiser/Customers(${customerId})/Accounts(${accountId})/Campaigns(${campaignId})/AdGroups(${Id})/Default.UpdateBidAndResetKeywordSource`;

  const uiBlocker = withUiBlock(blockerEl);
  odata.patch(uri, { Bid: newBid })
    .then(() => {
      popupUtil.close();
      // refresh row
      applyCallback(bidKV);
      activity.trace(
        `Apply success for ${Id} [${Name}]: ${oldValue} -> ${newBid}, Currency:${currency}, DataDate: ${startDate}~${endDate}, TrackingId: ${trackingId}, IsCustomBid: ${isCustomBid}, DisplayBiddingStrategyType: ${bidType}`,
        'customApply'
      );
      uiBlocker();
    }, (error) => {
      let errorMessage = '';
      if (!error || !error.errorResponseBody || error.errorResponseBody.value.length === 0) {
        errorMessage = i18n.getString('ErrorNumber_-1');
      } else {
        const errorCode = error.errorResponseBody.value[0].Code;

        errorMessage = i18n.getString(`Error_Campaign_API_Bidlandscape_CustomApply_${errorCode}`) ||
          i18n.getString(`Error_Campaign_API_${errorCode}`);
      }
      showError(errorMessage);
      uiBlocker();
      popupUtil.close();
    });
}

function getCurrentAppliedBid(adgroup) {
  const {
    DisplayBiddingStrategyType: bidType,
  } = adgroup;
  const bidKey = BiddingStrategyToBidParam[bidType] || 'SearchBid';
  return adgroup[bidKey];
}

function fetchAdGroupBidLandscape(adgroupId, scope) {
  const { customerId: cid, accountId: aid, userId: uid } = scope;
  const uri = url.queryify('/Campaign/BidLandscape/FetchAdGroupBidLandscapeData', {
    cid,
    uid,
    aid,
    __adcenterAJAX: 'true',
  });

  return http.post(uri, {
    contentType: 'application/x-www-form-urlencoded',
    data: {
      adgroupId,
      __RequestVerificationToken: $('input[name="__RequestVerificationToken"]').val(),
    },
  });
}

function validateBid(newBidStr, bidType) {
  const bidRangeKey = BiddingStrategyToRangeKey[bidType] || 'KeywordBidRange';
  const bidRange = appConfig.get(bidRangeKey);
  const bid = i18n.parseDecimal(newBidStr);
  const floorPrice = bidRange.FloorValue;
  const ceilingPrice = bidRange.CeilingValue;

  if (bid < floorPrice) {
    // @April please avoid to use this method for i18n,
    // the same to other places, I just update to make it work temp
    return i18n.getString('AdGroupSummary_SearchBidLessThanFloorPrice').replace('#0#', i18n.formatDecimalToFixed(floorPrice));
  } else if (bid > ceilingPrice) {
    return i18n.getString('AdGroupSummary_SearchBidMoreThanCeilingPrice').replace('#0#', i18n.formatDecimalToFixed(ceilingPrice));
  }
  return '';
}

function logBidLandscape() {
  const { adgroup, adgroupBidLandscapeView, trackingId } = this;
  const selectMode = adgroupBidLandscapeView.viewModel.adgroupSelectMode();
  if (selectMode !== 'UpdateDefault') {
    adgroupBidLandscapeView.off('selectModeChange', logBidLandscape);
  }
  const activity = senario.fetch.create();
  const ariaData = {
    Id: adgroup.Id,
    ActionName: 'ViewAdGroupBidLandscape',
    UIData: {
      selectMode,
      bidList: adgroupBidLandscapeView.viewModel.dataArray(),
      currencyCode,
      currentBid: getCurrentAppliedBid(adgroup),
      startDate: adgroupBidLandscapeView.viewModel.startDateString(),
      endDate: adgroupBidLandscapeView.viewModel.endDateString(),
      trackingId,
    },
  };
  activity.trace(ariaData, 'ViewAdGroupBidLandscape');
}

function onCallReturnedForBidLandscape({
  adgroup,
  response,
  event,
  scope,
  container,
  isForVNext,
  applyCallback,
  applyBlockerEl,
  inKeywordCreationWorkflow,
}) {
  const trackingId = generateGuid();
  const originalBid = getCurrentAppliedBid(adgroup);
  const adgroupBidLandscapeView = new AdGroupBidLandscape({
    parentItem: {
      Id: adgroup.Id,
      CurrentValue: originalBid.toString(),
      EntityName: adgroup.Name,
      campaignType: adgroup.Campaign.CampaignType,
    },
    i18n,
    dataToShow: response,
    isForVNext,
    inKeywordCreationWorkflow,
    helpId: inKeywordCreationWorkflow ? 'app54679' : null,
  });

  const { DisplayBiddingStrategyType: bidType } = adgroup;

  adgroupBidLandscapeView.on('cancel', () => {
    popupUtil.close();
  });

  adgroupBidLandscapeView.on('update', ({ valueToApply }) => {
    // check whether input is withing bid allowed range
    adgroupBidLandscapeView.markInputValueInvalid(validateBid(valueToApply, bidType));
  });

  adgroupBidLandscapeView.on('apply', () => {
    // for clear custom bid, let user confirm whether they want to clear all keywords bid
    if (adgroupBidLandscapeView.clearBid()) {
      // confirm whether user want to clear all keyword's custom bids
      // eslint-disable-next-line  no-alert
      const toSave = window.confirm(i18n.getString('AdGroupBidLandscape_Uniform_SaveConfirmMessageBox'));
      if (!toSave) {
        return;
      }
    }

    const activity = adgroupBidLandscapeView.clearBid() ?
      senario.applyBidLandscapeAndClearCustomBid.create() :
      senario.applyBidLandscapeWitoutClearCustomBid.create();

    activity.trace(
      `Getting bid update request from bid landscape, original bid is: ${originalBid}, value trying to apply is:${adgroupBidLandscapeView.valueToApply()}`,
      'ApplyAdGroupBidLandsapeFromCampaignUI'
    );
    if (adgroupBidLandscapeView.clearBid()) {
      customApply(
        adgroup,
        adgroupBidLandscapeView,
        activity,
        scope,
        applyCallback,
        applyBlockerEl,
        trackingId
      );
    } else {
      defaultApply(
        adgroup,
        adgroupBidLandscapeView,
        activity,
        applyCallback,
        applyBlockerEl,
        trackingId
      );
    }
  });

  adgroupBidLandscapeView.on('selectModeChange', logBidLandscape, { adgroup, adgroupBidLandscapeView, trackingId });

  adgroupBidLandscapeView.render();

  if (adgroupBidLandscapeView.hasData()) {
    popupUtil.render(adgroupBidLandscapeView.$el);
  } else {
    showPopup({
      positioningMethod: 'fit',
      positioningOptions: {
        anchorPosition: {
          top: event.clientY,
          left: event.clientX,
          width: $(event.target).width(),
          height: $(event.target).height(),
        },
      },
      contentView: adgroupBidLandscapeView,
      i18n,
      classes: ['bidlandscape-container'],
      container,
      anchor: $(event.target),
    });
  }
}

/**
 * show bid landscape panel, and bind alll related actions including apply/cancel
 *
 * @param {Object} event - The backbone click event from where the quality score cell was clicked.
 * @param {Object} adgroup - adgroup entity that triggered this call
 * @param {Object} scope - scope which includes {customerId, accountId}
 * @param {Object} applyCallback - callback function after apply
 * @param {Object} inKeywordCreationWorkflow -
 *  boolean to indicate if the landscape is showed on creation workflow
 * @returns {undefined}.
*/
export function openAdGroupBidLandscape({
  event,
  scope,
  adgroup,
  container,
  isForVNext,
  applyCallback,
  applyBlockerEl,
  inKeywordCreationWorkflow = false,
}) {
  // close previous one, and stop other bindings
  popupUtil.close();
  event.stopImmediatePropagation();

  fetchAdGroupBidLandscape(adgroup.Id, scope).then((res) => {
    onCallReturnedForBidLandscape({
      adgroup,
      response: res,
      event,
      scope,
      container,
      isForVNext,
      applyCallback,
      applyBlockerEl,
      inKeywordCreationWorkflow,
    });
    const activity = senario.fetch.create();
    activity.trace(`Fetch AdGroup bid landscape success for ${adgroup.Id}`, 'adgroupBidlandscape');
  }).catch(() => {
    onCallReturnedForBidLandscape({
      adgroup,
      response: {},
      event,
      scope,
      container,
      isForVNext,
      applyCallback,
      applyBlockerEl,
      inKeywordCreationWorkflow,
    });
  }).finally(withUiBlock($(event.target).parent()));
}
